import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { OrgService, type UpdateOrgMasterDataPayload } from '@/services/OrgService';
import { AccountService } from '@/services/AccountService';
import { OrgKnowledgeService } from '@/services/OrgKnowledgeService';
import { CanvasService } from '@/services/CanvasService';
import type { GenerateCanvasBody, RorosCanvasBlocksDTO, RorosCanvasResponseDTO, RorosCanvasType } from '@/types/roros/canvases';

// Query Keys
export const queryKeys = {
  session: ['session'] as const,
  orgs: ['orgs'] as const,
  orgMembers: (orgId: string) => ['org', orgId, 'members'] as const,
  orgInvites: (orgId: string) => ['org', orgId, 'invites'] as const,
  orgBackgrounds: (orgId: string) => ['org', orgId, 'backgrounds'] as const,
  orgDashboardStats: (orgId: string) => ['org', orgId, 'dashboard', 'stats'] as const,
  orgDashboardActivity: (orgId: string) => ['org', orgId, 'dashboard', 'activity'] as const,
  drafts: (filters: Record<string, any>) => ['drafts', filters] as const,
  adminJobs: (filters: Record<string, any>) => ['admin', 'jobs', filters] as const,
  adminJobsStats: (window: string) => ['admin', 'jobs', 'stats', window] as const,
  adminJob: (jobId: string) => ['admin', 'job', jobId] as const,
  adminJobEvents: (jobId: string) => ['admin', 'job', jobId, 'events'] as const,
  rorosCanvas: (type: RorosCanvasType) => ['roros', 'canvases', type] as const,
} as const;


// ROROS Canvases
export function useRorosCanvas(type: RorosCanvasType) {
  return useQuery({
    queryKey: queryKeys.rorosCanvas(type),
    queryFn: () => CanvasService.get(type),
    staleTime: 2 * 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useSaveRorosCanvas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, blocks }: { type: RorosCanvasType; blocks: RorosCanvasBlocksDTO }) =>
      CanvasService.save(type, blocks),
    onMutate: async ({ type, blocks }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.rorosCanvas(type) });
      const previous = queryClient.getQueryData<RorosCanvasResponseDTO>(queryKeys.rorosCanvas(type));
      if (previous) {
        queryClient.setQueryData(queryKeys.rorosCanvas(type), {
          ...previous,
          canvas: { ...previous.canvas, blocks },
        });
      }
      return { previous };
    },
    onError: (_err, { type }, context) => {
      if (context?.previous) queryClient.setQueryData(queryKeys.rorosCanvas(type), context.previous);
    },
    onSettled: (_data, _err, { type }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rorosCanvas(type) });
    },
  });
}

export function useGenerateRorosCanvas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, body }: { type: RorosCanvasType; body: GenerateCanvasBody }) =>
      CanvasService.generate(type, body),
    onSuccess: (data: RorosCanvasResponseDTO, { type }) => {
      queryClient.setQueryData(queryKeys.rorosCanvas(type), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.rorosCanvas(type) });
    },
  });
}

// Org Members Query
export function useOrgMembers(orgId?: string) {
  const svc = new OrgService();
  return useQuery({
    queryKey: queryKeys.orgMembers(orgId || ''),
    queryFn: () => svc.listMembers(orgId!),
    enabled: !!orgId,
    staleTime: 2 * 60_000, // 2 minutes
    gcTime: 5 * 60_000,
  });
}

// Org Invites Query
export function useOrgInvites(orgId?: string) {
  const svc = new OrgService();
  return useQuery({
    queryKey: queryKeys.orgInvites(orgId || ''),
    queryFn: () => svc.listInvites(orgId!),
    enabled: !!orgId,
    staleTime: 2 * 60_000,
    gcTime: 5 * 60_000,
  });
}

// Org Backgrounds Query
// useOrgBackgrounds removed in Milestone 3

// Admin Jobs Query
export function useAdminJobs(filters: Record<string, any> = {}) {
  return useQuery({
    queryKey: queryKeys.adminJobs(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, String(value));
      });
      const res = await apiFetch(`/api/admin/jobs?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const json = await res.json();
      return json.success ? { items: json.data, pagination: json.pagination } : { items: [], pagination: null };
    },
    staleTime: 30_000, // 30 seconds for admin data
    gcTime: 2 * 60_000,
  });
}

// Admin Jobs Stats Query
export function useAdminJobsStats(window: string = '24h') {
  return useQuery({
    queryKey: queryKeys.adminJobsStats(window),
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/jobs/stats?window=${window}`);
      if (!res.ok) throw new Error('Failed to fetch stats');
      const json = await res.json();
      return json.success ? json.data : null;
    },
    staleTime: 60_000, // 1 minute for stats
    gcTime: 5 * 60_000,
  });
}

// Admin Job Details Query
export function useAdminJob(jobId?: string) {
  return useQuery({
    queryKey: queryKeys.adminJob(jobId || ''),
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/jobs/${jobId}`);
      if (!res.ok) throw new Error('Failed to fetch job');
      const json = await res.json();
      return json.success ? json.data : null;
    },
    enabled: !!jobId,
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  });
}

// Admin Job Events Query
export function useAdminJobEvents(jobId?: string) {
  return useQuery({
    queryKey: queryKeys.adminJobEvents(jobId || ''),
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/jobs/${jobId}/events`);
      if (!res.ok) throw new Error('Failed to fetch events');
      const json = await res.json();
      return json.success ? json.data : [];
    },
    enabled: !!jobId,
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  });
}

// Mutation for Organization Master Data (Daten tab)
export function useOrgMasterDataUpdate() {
  const queryClient = useQueryClient();
  const svc = new OrgService();

  return useMutation({
    mutationFn: ({ orgId, payload }: { orgId: string; payload: UpdateOrgMasterDataPayload }) =>
      svc.updateMasterData(orgId, payload),
    onSuccess: (_, { orgId }) => {
      // Refresh the Zustand org store so the sidebar / org switcher show the
      // new name, etc. — orgs list isn't cached via react-query.
      void useOrgStore.getState().fetchOrgs();
      // Invalidate any react-query caches that might be reading org data.
      queryClient.invalidateQueries({ queryKey: queryKeys.orgs });
      queryClient.invalidateQueries({ queryKey: ['org', orgId] });
    },
  });
}

// Mutations for Members & Invites
export function useInviteMemberMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();
  
  return useMutation({
    mutationFn: ({ orgId, email, roleIds }: { orgId: string; email: string; roleIds: string[] }) =>
      svc.invite(orgId, email, roleIds),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgInvites(orgId) });
    },
  });
}

export function useUpdateMemberRoleMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();
  
  return useMutation({
    mutationFn: ({ orgId, userId, role }: { orgId: string; userId: string; role: 'admin' | 'member' | 'owner' }) =>
      svc.updateMemberRole(orgId, userId, role),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers(orgId) });
    },
  });
}

export function useRemoveMemberMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();
  
  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      svc.removeMember(orgId, userId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers(orgId) });
    },
  });
}

export function useRevokeInviteMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();

  return useMutation({
    mutationFn: ({ orgId, inviteId }: { orgId: string; inviteId: string }) =>
      svc.revokeInvite(orgId, inviteId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgInvites(orgId) });
    },
  });
}

/** AC-Users-4: toggle org member active/inactive state */
export function useSetMemberActiveMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();

  return useMutation({
    mutationFn: ({ orgId, userId, isActive }: { orgId: string; userId: string; isActive: boolean }) =>
      svc.setMemberActive(orgId, userId, isActive),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers(orgId) });
    },
  });
}

// Hook to get active org ID from store
export function useActiveOrgId() {
  return useOrgStore(s => s.activeOrgId);
}

// Account/Profile queries
export function useAccountProfile() {
  const svc = new AccountService();
  return useQuery({
    queryKey: ['account', 'profile'] as const,
    queryFn: () => svc.getProfile(),
    staleTime: 5 * 60_000, // 5 minutes
    gcTime: 10 * 60_000,
  });
}

export function useAccountSessions() {
  const svc = new AccountService();
  return useQuery({
    queryKey: ['account', 'sessions'] as const,
    queryFn: () => svc.listSessions(),
    staleTime: 2 * 60_000, // 2 minutes
    gcTime: 5 * 60_000,
  });
}

// Organization Knowledge Profile query
export function useOrgKnowledgeProfile() {
  const svc = new OrgKnowledgeService();
  return useQuery({
    queryKey: ['orgKnowledge', 'profile'] as const,
    queryFn: () => svc.getProfile(),
    staleTime: 5 * 60_000, // 5 minutes
    gcTime: 10 * 60_000,
  });
}

// Account mutations
export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  const svc = new AccountService();
  
  return useMutation({
    mutationFn: (data: { fullName: string }) => svc.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'profile'] });
    },
  });
}

export function useRevokeSessionMutation() {
  const queryClient = useQueryClient();
  const svc = new AccountService();
  
  return useMutation({
    mutationFn: (sessionId: string) => svc.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] });
    },
  });
}

// Organization Knowledge mutations
export function useSaveOrgKnowledgeProfileMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgKnowledgeService();
  
  return useMutation({
    mutationFn: (data: any) => svc.saveProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgKnowledge', 'profile'] });
    },
  });
}
